import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    X, 
    User, 
    UserCheck, 
    FileText, 
    Download,
    AlertCircle 
} from 'lucide-react'; 
import api from '../services/api';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
const CitesModal = ({ reptile, user, onClose }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    
    // Stato del form pre-compilato con i dati DB
    const [formData, setFormData] = useState({
        signerDetails: {
            name: '', 
            surname: '',
            address: '',
            city: '',
            province: '',
            phoneNumber: user?.phoneNumber || '',
            email: user?.email || '',
            cap: '' 
        },
        receiverDetails: {
            name: '',
            surname:  '',
            address: '',
            city: '',   
            province: '',
            phone:'',
            email:  '',
            cap: '',
        },
        extraDetails: {
            place: user?.billingDetails?.address?.city || 'Roma',
            date: new Date().toISOString().split('T')[0],
            originCountry: 'IT'
        },
        options: {
            includeProfilePic: false,
            avatarUrl: user?.avatar || ''
        }
    });

    const handleChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

   const handleDownload = async () => {
    setLoading(true);
    
    try {
        const response = await api.post(`/reptile/download-cites/${reptile._id}`, formData, {
            responseType: 'blob'
        });

        // Se sei su Web, mantieni la logica attuale
if (!Capacitor.isNativePlatform()) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${t('CitesModal.fileNamePrefix')}${reptile.name}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } else {
            // Logica per Mobile (Capacitor)
            const reader = new FileReader();
            reader.readAsDataURL(response.data);
            reader.onloadend = async () => {
                const base64Data = reader.result.split(',')[1];
                
                const savedFile = await Filesystem.writeFile({
                    path: `${t('CitesModal.fileNamePrefix')}${reptile.name}.pdf`,
                    data: base64Data,
                    directory: Directory.Documents, // O Directory.Cache
                });

                alert(`${t('CitesModal.successDownload')} ${savedFile.uri}`);
                
            };
        }
        
        onClose();
    } catch (error) {
        console.error("Errore download:", error);
        alert(t('CitesModal.errorDownload'));
    } finally {
        setLoading(false);
    }
};

    // Stile comune per gli input
    const inputClasses = "w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400";
    const labelClasses = "block text-xs font-medium text-gray-500 mb-1 ml-1";

    const noteText = t('CitesModal.note');

    return (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all duration-300">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-white">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            <FileText className="w-5 h-5 text-blue-600" />
                            {t('CitesModal.title')}
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">{t('CitesModal.subtitle')}</p>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body Scrollable */}
                <div className="p-6 overflow-y-auto custom-scrollbar space-y-8">

                    {noteText && (
                        <div className="p-4 bg-amber-50 border-l-4 border-amber-500 text-amber-800 rounded-r-xl font-medium flex items-center gap-3">
                            <AlertCircle className="text-amber-500 w-5 h-5 flex-shrink-0" />
                            <span className="text-sm">{noteText}</span>
                        </div>
                    )}

                    {/* Sezione Cedente */}
                    <section className="space-y-4">
                        <div className="flex items-center gap-2 text-gray-800 border-b pb-2 border-gray-100">
                            <div className="p-1.5 bg-blue-50 rounded-md text-blue-600">
                                <User className="w-4 h-4" />
                            </div>
                            <h3 className="font-semibold text-sm uppercase tracking-wide">{t('CitesModal.senderSection')}</h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClasses}>{t('CitesModal.name')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.namePlaceholderSender')} className={inputClasses}
                                    value={formData.signerDetails.name}
                                    onChange={(e) => handleChange('signerDetails', 'name', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={labelClasses}>{t('CitesModal.surname')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.surnamePlaceholderSender')} className={inputClasses}
                                    value={formData.signerDetails.surname}
                                    onChange={(e) => handleChange('signerDetails', 'surname', e.target.value)}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClasses}>{t('CitesModal.address')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.addressPlaceholder')} className={inputClasses}
                                    value={formData.signerDetails.address}
                                    onChange={(e) => handleChange('signerDetails', 'address', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={labelClasses}>{t('CitesModal.city')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.cityPlaceholder')} className={inputClasses}
                                    value={formData.signerDetails.city}
                                    onChange={(e) => handleChange('signerDetails', 'city', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={labelClasses}>{t('CitesModal.province')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.provincePlaceholder')} className={inputClasses}
                                    value={formData.signerDetails.province}
                                    maxLength={2}
                                    onChange={(e) => handleChange('signerDetails', 'province', e.target.value)}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Sezione Ricevente */}
                    <section className="space-y-4">
                        <div className="flex items-center gap-2 text-gray-800 border-b pb-2 border-gray-100">
                            <div className="p-1.5 bg-green-50 rounded-md text-green-600">
                                <UserCheck className="w-4 h-4" />
                            </div>
                            <h3 className="font-semibold text-sm uppercase tracking-wide">{t('CitesModal.receiverSection')}</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClasses}>{t('CitesModal.name')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.namePlaceholderReceiver')} className={inputClasses}
                                    value={formData.receiverDetails.name}
                                    onChange={(e) => handleChange('receiverDetails', 'name', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className={labelClasses}>{t('CitesModal.surname')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.surnamePlaceholderReceiver')} className={inputClasses}
                                    value={formData.receiverDetails.surname}
                                    onChange={(e) => handleChange('receiverDetails', 'surname', e.target.value)}
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClasses}>{t('CitesModal.fullAddress')}</label>
                                <input 
                                    type="text" placeholder={t('CitesModal.addressPlaceholder')} className={inputClasses}
                                    value={formData.receiverDetails.address}
                                    onChange={(e) => handleChange('receiverDetails', 'address', e.target.value)}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4 md:col-span-2">
                                <div>
                                    <label className={labelClasses}>{t('CitesModal.city')}</label>
                                    <input 
                                        type="text" placeholder={t('CitesModal.cityPlaceholder')} className={inputClasses}
                                        value={formData.receiverDetails.city}
                                        onChange={(e) => handleChange('receiverDetails', 'city', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className={labelClasses}>{t('CitesModal.province')}</label>
                                    <input 
                                        type="text" placeholder={t('CitesModal.provincePlaceholder')} className={inputClasses}
                                        value={formData.receiverDetails.province}
                                        maxLength={2}
                                        onChange={(e) => handleChange('receiverDetails', 'province', e.target.value)}
                                    />
                                </div>
                            </div>
                             <div className="grid grid-cols-2 gap-4 md:col-span-2">
                                <div>
                                    <label className={labelClasses}>{t('CitesModal.phone')}</label>
                                    <input 
                                        type="text" placeholder="+39..." className={inputClasses}
                                        value={formData.receiverDetails.phone}
                                        onChange={(e) => handleChange('receiverDetails', 'phone', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className={labelClasses}>{t('CitesModal.email')}</label>
                                <input 
                                    type="email" placeholder="email@esempio.com" className={inputClasses}
                                    value={formData.receiverDetails.email}
                                    onChange={(e) => handleChange('receiverDetails', 'email', e.target.value)}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Opzioni */}
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center gap-3 hover:bg-gray-100 transition-colors cursor-pointer" 
                         onClick={() => handleChange('options', 'includeProfilePic', !formData.options.includeProfilePic)}>
                        <input 
                            type="checkbox" 
                            id="profilePic"
                            checked={formData.options.includeProfilePic}
                            onChange={(e) => handleChange('options', 'includeProfilePic', e.target.checked)}
                            className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                        />
                        <label htmlFor="profilePic" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                            {t('CitesModal.includeProfilePic')}
                        </label>
                    </div>

                </div>

                {/* Footer Actions */}
                <div className="px-6 py-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3 sticky bottom-0">
                    <button 
                        onClick={onClose}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:outline-none focus:ring-gray-100 transition-all"
                    >
                        {t('CitesModal.cancel')}
                    </button>
                    <button 
                        onClick={handleDownload}
                        disabled={loading}
                        className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-500/50 shadow-lg shadow-blue-600/30 disabled:opacity-50 disabled:shadow-none flex items-center gap-2 transition-all"
                    >
                        {loading ? (
                            <>{t('CitesModal.generating')}</>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                {t('CitesModal.downloadBtn')}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CitesModal;